/**
 * What each free element type is, in one record keyed by `customAreaType`:
 * how an element of the type is made, how it stands, whether it is built in,
 * and what it does to the floor. The constructors, the edits, the scene, the
 * measurements and the save optimizer read these facts here instead of
 * naming types, so a new type is its schema branch plus one entry.
 *
 * A type this version doesn't know (a newer version wrote it) reads as a
 * plain box: it stands on its base and moves and resizes like an object, is
 * never built in, so stays out of measurements, and is saved back unchanged.
 */
import {
  type CustomAreaType,
  type FloorPlanFreeElement,
  freeElementBase,
  isKnownCustomAreaType,
} from './schemas';

/** Standard US counter height. */
export const COUNTER_HEIGHT = 3;
/** The height a free object stands when it names none. */
export const DEFAULT_OBJECT_HEIGHT = 2;
/** A new counter's footprint (ft): 24 in × 24 in. */
export const DEFAULT_COUNTER_SIZE = 2;

/**
 * What an element's footprint does to the room's floor (`resolveScene`):
 * flooring `retypes` the floor under it; an interior wall `carves` it out,
 * and its outline becomes wall; a counter `covers` it with countertop; the
 * rest take no part of their own (`none`), though a built-in one still comes
 * out of the finished floor through the built-in rule.
 */
export type FloorRole = 'retypes' | 'carves' | 'covers' | 'none';

/** The facts every element of one type shares. */
export interface FreeElementType {
  floorRole: FloorRole;
  /** Whether it moves up and down: it stands on its base, where flooring lies on the floor and a wall runs floor to ceiling. */
  movesInHeight: boolean;
  /** How tall an element of the type stands when it names no height; absent for a type that doesn't stand on its base. */
  defaultHeight?: number;
  /** The vertical span [bottom, top] an element of the type occupies under a ceiling this high. */
  span: (element: FloorPlanFreeElement, ceiling: number) => [number, number];
  /** Whether an element of the type is part of the house rather than movable. */
  builtIn: (element: FloorPlanFreeElement) => boolean;
  /** Whether it carries an object's `subtype`, `name` and `notBuiltIn`. */
  objectFields?: boolean;
  /** Fields `makeFreeElement` gives every element of the type, ahead of its box. */
  madeWith?: Partial<FloorPlanFreeElement>;
  /** Fields an element the editor adds (`defaultFreeElement`) gets on top of those. */
  addedWith?: Partial<FloorPlanFreeElement>;
  /** The side (ft) of the square the editor adds; left out, a third of the room's smaller side. */
  addedSize?: number;
  /** The type's own fields that read the same left out at these values, beside `FREE_ELEMENT_FIELD_DEFAULTS`: the save optimizer drops them. */
  fieldDefaults?: Record<string, unknown>;
}

type ObjectElement = Extract<FloorPlanFreeElement, { customAreaType: 'object' }>;
type FlooringElement = Extract<FloorPlanFreeElement, { customAreaType: 'flooring' }>;

const never = () => false;
const always = () => true;

/** A type that stands on its base and moves up and down, `defaultHeight` tall when an element names no height. */
const standingOnBase = (defaultHeight: number): Pick<FreeElementType, 'movesInHeight' | 'defaultHeight' | 'span'> => ({
  movesInHeight: true,
  defaultHeight,
  span: element => {
    const base = freeElementBase(element);
    return [base, base + (element.height ?? defaultHeight)];
  },
});

const FREE_ELEMENT_TYPES: Record<CustomAreaType, FreeElementType> = {
  flooring: { floorRole: 'retypes', movesInHeight: false, span: () => [0, 0], builtIn: never, fieldDefaults: { floorType: '' } },
  counter: { floorRole: 'covers', ...standingOnBase(COUNTER_HEIGHT), builtIn: always, madeWith: { height: COUNTER_HEIGHT }, addedSize: DEFAULT_COUNTER_SIZE },
  wall: { floorRole: 'carves', movesInHeight: false, span: (_element, ceiling) => [0, ceiling], builtIn: always },
  object: {
    floorRole: 'none',
    ...standingOnBase(DEFAULT_OBJECT_HEIGHT),
    builtIn: element => !isMovableObject(element),
    objectFields: true,
    addedWith: { height: DEFAULT_OBJECT_HEIGHT, subtype: 'furniture', notBuiltIn: true },
    fieldDefaults: { notBuiltIn: false },
  },
};

const UNKNOWN_FREE_ELEMENT_TYPE: FreeElementType = { floorRole: 'none', ...standingOnBase(DEFAULT_OBJECT_HEIGHT), builtIn: never };

/** The facts of the type named `customAreaType`; a type this version doesn't know is the plain box. */
export const freeElementTypeOf = (customAreaType: string): FreeElementType =>
  isKnownCustomAreaType(customAreaType) ? FREE_ELEMENT_TYPES[customAreaType] : UNKNOWN_FREE_ELEMENT_TYPE;

/** The facts of an element's type. */
export const freeElementType = (element: FloorPlanFreeElement): FreeElementType => freeElementTypeOf(element.customAreaType);

/** Whether the element carries an object's `subtype`, `name` and `notBuiltIn`. */
export const hasObjectFields = (element: FloorPlanFreeElement): element is ObjectElement => freeElementType(element).objectFields === true;

/** Whether the element retypes the floor under it: flooring, which carries a `floorType`. */
export const retypesFloor = (element: FloorPlanFreeElement): element is FlooringElement => freeElementType(element).floorRole === 'retypes';

/** Whether the element is an appliance: an object of subtype `appliance`. */
export const isAppliance = (element: FloorPlanFreeElement): boolean => hasObjectFields(element) && element.subtype === 'appliance';

/** The vertical span [bottom, top] an element occupies: flooring lies on the floor, a wall runs floor to ceiling, and the rest stand on their base. */
export const freeElementSpan = (element: FloorPlanFreeElement, ceiling: number): [number, number] => freeElementType(element).span(element, ceiling);

/** How tall an element that stands on its base is: its own height, or its type's default. */
export const freeElementHeight = (element: FloorPlanFreeElement): number => element.height ?? freeElementType(element).defaultHeight ?? 0;

/**
 * Whether an element can move up and down: everything that stands on its base
 * in `freeElementSpan`. That is an object, a counter, or a type this version
 * doesn't know, which acts as a plain box. Flooring lies on the floor and a
 * wall runs floor to ceiling.
 */
export const movesInHeight = (element: FloorPlanFreeElement): boolean => freeElementType(element).movesInHeight;

/**
 * Whether an object is movable rather than part of the house: its
 * `notBuiltIn` flag is `true`. An object with no flag is built in, whatever its
 * subtype. False for anything but an object.
 */
export const isMovableObject = (element: FloorPlanFreeElement): boolean => hasObjectFields(element) && element.notBuiltIn === true;
